import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container, Row, Col, Card, Button, Badge, ProgressBar,
  Spinner, Alert, Modal, Form, Table, OverlayTrigger, Tooltip,
} from 'react-bootstrap';
import api from '../../services/api';
import { notify } from '../../stores/useNotificationStore';
import SearchSelectField, { SearchOption } from '../../components/SearchSelectField';
import { FiPaperclip, FiX } from '../../utils/icons';
import * as maturidade from '../../utils/maturidade';

// ── Types ──────────────────────────────────────────────────────────────────────

type TipoResposta = 'SIM_NAO' | 'PERCENTUAL' | 'TEXTO_LIVRE' | 'MULTIPLA_ESCOLHA';

interface Condicao {
  id: number;
  questionario_pergunta_id: number;
  resposta_valor: string;
  acao: 'CHECKLIST' | 'COMPLEMENTAR' | 'PROXIMA_PRINCIPAL' | 'FIM';
  ordem: number;
  pergunta_complementar_qp_id: number | null;
}

interface QP {
  qp_id: number;
  pergunta_id: number;
  pergunta_text: string;
  tipo_resposta: TipoResposta;
  permite_na: boolean;
  usa_nivel_controle?: boolean;
  nivel_controle?: NivelControle | null;
  controle_nome?: string;
  controle_indice?: string;
  controle_descricao?: string;
  peso: number;
  checklist_id: number | null;
  nivel: 'principal' | 'complementar';
  ordem: number;
  condicoes: Condicao[];
}

type ChecklistItemResposta = 'SIM' | 'NAO' | 'NA';
type NivelControle = 'MANDATORIO' | 'RECOMENDADO' | 'DESEJAVEL';
type DetalheTipo = 'PLANO' | 'EVIDENCIA' | 'JUSTIFICATIVA';

interface ChecklistItemDetalhe {
  justificativa?: string | null;
  plano_data_limite?: string | null;
  plano_responsavel_nome?: string | null;
  plano_responsavel_email?: string | null;
  plano_descricao?: string | null;
  evidencia_nome?: string | null;
  evidencia_comentario?: string | null;
}

// Regra do campo "Descrição do Plano de Ação": texto livre, mínimo 15 e máximo 120 caracteres.
const PLANO_DESCRICAO_MIN = 15;
const PLANO_DESCRICAO_MAX = 120;
// Comentário livre junto da evidência (pergunta e item de checklist): opcional, sem mínimo.
const COMENTARIO_EVIDENCIA_MAX = 200;
const planoDescricaoValida = (v: string | null | undefined): boolean => {
  const len = (v || '').trim().length;
  return len >= PLANO_DESCRICAO_MIN && len <= PLANO_DESCRICAO_MAX;
};

// Regra: para cada nível de controle + resposta, qual informação extra é exigida.
// Desejável nunca exige nada. Evidência é opcional; Plano e Justificativa obrigatórios.
const detalheExigido = (nivel: NivelControle | undefined | null, resposta: ChecklistItemResposta): DetalheTipo | null => {
  if (nivel === 'MANDATORIO') {
    if (resposta === 'NAO') return 'PLANO';
    if (resposta === 'SIM') return 'EVIDENCIA';
    if (resposta === 'NA') return 'JUSTIFICATIVA';
  }
  if (nivel === 'RECOMENDADO') {
    if (resposta === 'NAO') return 'JUSTIFICATIVA';
    if (resposta === 'SIM') return 'EVIDENCIA';
    if (resposta === 'NA') return 'JUSTIFICATIVA';
  }
  return null; // DESEJAVEL ou sem nível → sem ação
};

// Validação de e-mail conforme as regras do requisito.
const emailValido = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

interface ChecklistItem {
  id: number;
  descricao: string;
  ordem?: number;
  percentual_peso?: number;
  ativo?: boolean;
  nivel_controle?: NivelControle;
}

interface ChecklistRequisito {
  id: number;
  titulo: string;
  itens: ChecklistItem[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const getUserId = (): number => {
  try { return JSON.parse(localStorage.getItem('usuarioData') || '{}').id || 1; }
  catch { return 1; }
};

// ── Answer input component ────────────────────────────────────────────────────

interface AnswerInputProps {
  qp: QP;
  value: any;
  onChange: (v: any) => void;
  disabled?: boolean;
}

const AnswerInput: React.FC<AnswerInputProps> = ({ qp, value, onChange, disabled }) => {
  switch (qp.tipo_resposta) {
    case 'SIM_NAO':
      return (
        <div className="d-flex gap-2 mt-2">
          {['SIM', 'NAO'].map(opt => (
            <Button
              key={opt}
              size="sm"
              variant={value === opt ? (opt === 'SIM' ? 'success' : 'danger') : 'outline-secondary'}
              onClick={() => !disabled && onChange(opt)}
              disabled={disabled}
              style={{ minWidth: 60 }}
            >
              {opt === 'SIM' ? 'Sim' : 'Não'}
            </Button>
          ))}
          {(qp.permite_na || qp.usa_nivel_controle) && (
            <Button
              size="sm"
              variant={value === 'NA' ? 'secondary' : 'outline-secondary'}
              onClick={() => !disabled && onChange('NA')}
              disabled={disabled}
              style={{ minWidth: 60 }}
            >
              N/A
            </Button>
          )}
        </div>
      );
    case 'PERCENTUAL':
      return (
        <div className="mt-2">
          <Form.Range
            min={0} max={100} value={value ?? 50}
            onChange={e => onChange(parseInt(e.target.value, 10))}
            disabled={disabled}
          />
          <span className="text-muted ms-2">{value ?? 50}%</span>
        </div>
      );
    case 'TEXTO_LIVRE':
      return (
        <Form.Control
          as="textarea"
          rows={3}
          className="mt-2"
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          placeholder="Digite sua resposta..."
          disabled={disabled}
        />
      );
    case 'MULTIPLA_ESCOLHA':
      return (
        <Form.Select
          className="mt-2"
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          disabled={disabled}
        >
          <option value="">Selecione uma opção</option>
        </Form.Select>
      );
    default:
      return null;
  }
};

// ── Maturity finish screen ────────────────────────────────────────────────────

interface FinishScreenProps {
  perguntas: QP[];
  answers: Record<number, any>;
  cascadedIds: Set<number>;
  checklistAnswers: Record<number, Record<number, ChecklistItemResposta>>;
  checklistData: Record<number, ChecklistRequisito[]>;
  frameworkNome?: string;
  onBack: () => void;
  onFinish: () => void;
}

const FinishScreen: React.FC<FinishScreenProps> = ({
  perguntas, answers, cascadedIds, checklistAnswers, checklistData, frameworkNome, onBack, onFinish,
}) => {
  const [showDocModal, setShowDocModal] = useState(false);
  const [showDocPrompt, setShowDocPrompt] = useState(false);

  const respondidas = perguntas.filter(p => {
    const v = answers[p.qp_id];
    return v !== undefined && v !== null && v !== '';
  });

  const totalSim = respondidas.filter(p => answers[p.qp_id] === 'SIM').length;
  const totalNao = respondidas.filter(p => answers[p.qp_id] === 'NAO').length;
  const totalNaoCascata = respondidas.filter(p => answers[p.qp_id] === 'NAO' && cascadedIds.has(p.qp_id)).length;
  const totalNA = respondidas.filter(p => answers[p.qp_id] === 'NA').length;

  // Fórmula completa vive em src/utils/maturidade.ts (reaproveitada também pelo
  // Relatório de Auditoria) — aqui só encaminha pros dados já carregados neste
  // componente, preservando as mesmas assinaturas (p: QP) usadas pelo resto do arquivo.
  const getChecklistPct = (p: QP): number | null =>
    maturidade.getChecklistPct(p, checklistData[p.qp_id], checklistAnswers[p.qp_id] || {});

  const calcMaturity = () => maturidade.calcMaturity(respondidas, answers, checklistData, checklistAnswers);

  const maturity = calcMaturity();
  const naoConformes = respondidas.filter(p => {
    const v = answers[p.qp_id];
    const checklistPct = getChecklistPct(p);
    if (checklistPct !== null) return checklistPct < 70;
    return v === 'NAO' || (typeof v === 'number' && v < 70);
  });

  const getBarVariant = (pct: number) => {
    if (pct >= 80) return 'success';
    if (pct >= 60) return 'warning';
    return 'danger';
  };

  React.useEffect(() => {
    if (naoConformes.length > 0 && maturity < 70) {
      setShowDocPrompt(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container className="py-4">
      <Card className="mb-4">
        <Card.Body className="text-center py-5">
          <div
            className="mx-auto mb-3 d-flex align-items-center justify-content-center"
            style={{ width: 64, height: 64, borderRadius: '50%', background: '#e6f6ec' }}
          >
            <span className="text-success fs-2">✓</span>
          </div>
          <h4 className="fw-bold mb-2">Questionário concluído!</h4>
          <p className="text-muted mb-4">
            Todas as perguntas foram respondidas. As perguntas marcadas como N/A não entram na
            contagem de conformidade.
          </p>

          <Row className="g-3 justify-content-center mb-3">
            <Col xs={6} md={3}>
              <div className="border rounded p-3 text-center h-100">
                <div className="fw-bold fs-4 text-primary">{respondidas.length}</div>
                <div className="text-muted small">Total respondidas</div>
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="border rounded p-3 text-center h-100">
                <div className="fw-bold fs-4 text-success">{totalSim}</div>
                <div className="text-muted small">Respostas SIM</div>
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="border rounded p-3 text-center h-100">
                <div className="fw-bold fs-4 text-danger">{totalNao}</div>
                <div className="text-muted small">Respostas NÃO</div>
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="border rounded p-3 text-center h-100">
                <div className="fw-bold fs-4 text-danger">{totalNaoCascata}</div>
                <div className="text-muted small">NÃO por cascata</div>
              </div>
            </Col>
          </Row>
          <Row className="g-3 justify-content-center">
            <Col xs={6} md={3}>
              <div className="border rounded p-3 text-center h-100">
                <div className="fw-bold fs-4 text-primary">{totalNA}</div>
                <div className="text-muted small">Não se aplica (N/A)</div>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Header as="h5" className="d-flex align-items-center gap-2">
          Relatório de Maturidade
          <Badge bg={getBarVariant(maturity)} className="ms-auto">{maturity.toFixed(1)}%</Badge>
        </Card.Header>
        <Card.Body>
          <div className="mb-3">
            <div className="d-flex justify-content-between mb-1">
              <small>Nível de Maturidade Geral</small>
              <small>{maturity.toFixed(1)}%</small>
            </div>
            <ProgressBar
              now={maturity}
              variant={getBarVariant(maturity)}
              style={{ height: 16 }}
              label={`${maturity.toFixed(0)}%`}
            />
          </div>

          {/* Maturity per question */}
          {perguntas.map(p => {
            const v = answers[p.qp_id];
            if (v === 'NA') {
              return (
                <div key={p.qp_id} className="mb-2">
                  <div className="d-flex justify-content-between mb-1">
                    <small className="text-truncate" style={{ maxWidth: '70%' }} title={p.pergunta_text}>
                      {p.pergunta_text}
                    </small>
                    <small className="text-muted">N/A</small>
                  </div>
                  <ProgressBar now={0} variant="secondary" style={{ height: 8 }} />
                </div>
              );
            }
            const checklistPct = getChecklistPct(p);
            let pct = 0;
            if (checklistPct !== null) pct = checklistPct;
            else if (v === 'SIM') pct = 100;
            else if (typeof v === 'number') pct = v;
            else if (v && v !== 'NAO') pct = 50;
            return (
              <div key={p.qp_id} className="mb-2">
                <div className="d-flex justify-content-between mb-1">
                  <small className="text-truncate" style={{ maxWidth: '70%' }} title={p.pergunta_text}>
                    {p.pergunta_text}
                  </small>
                  <small>{v !== undefined ? `${pct}%` : '—'}</small>
                </div>
                <ProgressBar
                  now={v !== undefined ? pct : 0}
                  variant={getBarVariant(pct)}
                  style={{ height: 8 }}
                />
              </div>
            );
          })}
        </Card.Body>
      </Card>

      {/* Non-conformity instructions */}
      {naoConformes.length > 0 && maturity < 70 && (
        <Alert variant="warning" className="mb-4">
          <Alert.Heading>Instrução de Conformidade</Alert.Heading>
          <p>
            A empresa não está em conformidade com o <strong>{frameworkNome || 'Framework'}</strong> nos
            requisitos abaixo:
          </p>
          <ul>
            {naoConformes.map(p => (
              <li key={p.qp_id}>{p.pergunta_text}</li>
            ))}
          </ul>
        </Alert>
      )}

      {/* Document offer */}
      {showDocPrompt && (
        <Card className="mb-4 border-info">
          <Card.Body>
            <p className="mb-3 fw-semibold">
              Gostaria de receber os documentos personalizados e adequados aos requisitos não conformes?
            </p>
            <div className="d-flex gap-2">
              <Button variant="success" onClick={() => { setShowDocModal(true); setShowDocPrompt(false); }}>
                Sim, quero receber
              </Button>
              <Button variant="outline-secondary" onClick={() => setShowDocPrompt(false)}>
                Não
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      <div className="d-flex gap-2 justify-content-between">
        <Button variant="secondary" onClick={onBack}>
          Voltar às Perguntas
        </Button>
        <Button variant="primary" onClick={onFinish}>
          Finalizar questionário
        </Button>
      </div>

      {/* Document selection modal */}
      <Modal show={showDocModal} onHide={() => setShowDocModal(false)} centered>
        <Modal.Header closeButton><Modal.Title>Selecionar Documentos</Modal.Title></Modal.Header>
        <Modal.Body>
          <p>Selecione os documentos que deseja receber para os requisitos não conformes:</p>
          {naoConformes.map(p => (
            <Form.Check
              key={p.qp_id}
              type="checkbox"
              label={p.pergunta_text}
              className="mb-2"
            />
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDocModal(false)}>Cancelar</Button>
          <Button variant="primary" onClick={() => { notify.success('Documentos solicitados!'); setShowDocModal(false); }}>
            Solicitar Documentos
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

// ── Main component ─────────────────────────────────────────────────────────────
//
// Modelo de navegação: o questionário avança por blocos, um por pergunta Principal.
// Um bloco começa com a Principal e vai revelando as Complementares configuradas
// (cadeia ligada pela condição SIM/RESPONDIDA → COMPLEMENTAR), conforme as regras:
//   1) Principal = NÃO ou N/A com Complementares → cascata NÃO/N/A para todas elas.
//   2) Principal = SIM com Complementares → revela as Complementares para resposta,
//      mantendo a Principal visível mas travada.
//   3) Complementar = NÃO → cascata NÃO para as Complementares restantes da cadeia.
//   4) Complementar = SIM → segue a regra do SIM, revela a próxima da cadeia.
//   5) Complementar = N/A → usa a regra do SIM (meswmo comportamento do item 4).
// Ao final do bloco (cascata ou cadeia esgotada) o sistema avança automaticamente
// para a próxima Principal quando o tipo de resposta é SIM/NÃO/N/A. Para o tipo
// Percentual (BARRA PORCENTAGEM) o avanço depende do botão "Continuar questionário".

interface AuditoriaItem {
  classificacao_conformidade_id: number | null;
  comentario_auditor: string | null;
  anexos: { id: number; arquivo_nome: string }[];
}

const AuditoriaItemVazio: AuditoriaItem = { classificacao_conformidade_id: null, comentario_auditor: null, anexos: [] };

const AnswerQuestionnairePage: React.FC<{ modoAuditoria?: boolean }> = ({ modoAuditoria = false }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const usuarioId = getUserId();

  // Questionnaire metadata
  const [questionnaire, setQuestionnaire] = useState<any>(null);
  const [perguntas, setPerguntas] = useState<QP[]>([]);

  // Lookup maps
  const [perguntasMap, setPerguntasMap] = useState<Map<number, QP>>(new Map());
  const [principals, setPrincipals] = useState<QP[]>([]);

  // Answer state
  const [answers, setAnswers] = useState<Record<number, any>>({});
  const [cascadedIds, setCascadedIds] = useState<Set<number>>(new Set());
  const [checklistAnswers, setChecklistAnswers] = useState<Record<number, Record<number, ChecklistItemResposta>>>({});
  const [checklistData, setChecklistData] = useState<Record<number, ChecklistRequisito[]>>({}); // qp_id → requisitos

  // Nível de Controle: detalhe (Plano/Evidência/Justificativa) por PERGUNTA
  const [respostaDetalhes, setRespostaDetalhes] = useState<Record<number, ChecklistItemDetalhe>>({}); // qp_id → detalhe
  const [pendingNivel, setPendingNivel] = useState<{ qpId: number; value: any } | null>(null);
  const [evidenciaSaving, setEvidenciaSaving] = useState<number | null>(null);
  const [sugerindoPlano, setSugerindoPlano] = useState<number | null>(null); // qp_id sendo sugerido agora

  // Auditoria (SM-004): dados por pergunta PRINCIPAL/controle (qp_id), só usados em modoAuditoria.
  const [auditoriaDados, setAuditoriaDados] = useState<Record<number, AuditoriaItem>>({});
  const [classificacoesConformidade, setClassificacoesConformidade] = useState<SearchOption[]>([]);
  const [auditoriaUploading, setAuditoriaUploading] = useState(false);

  // Nível de Controle: detalhe (Plano/Evidência/Justificativa) por ITEM do checklist
  const [checklistDetalhes, setChecklistDetalhes] = useState<Record<number, Record<number, ChecklistItemDetalhe>>>({}); // qp_id → item_id → detalhe
  const [itemDetalheModal, setItemDetalheModal] = useState<{ qpId: number; itemId: number; tipo: DetalheTipo } | null>(null);
  const [evidenciaSavingItem, setEvidenciaSavingItem] = useState<number | null>(null); // item_id em upload

  // Block navigation state (um bloco = uma Principal + suas Complementares reveladas)
  const [principalIndex, setPrincipalIndex] = useState(0);
  const [visibleExtra, setVisibleExtra] = useState<number[]>([]); // qp_ids de complementares reveladas no bloco atual
  // NÃO/N/A com cascata: mostra as complementares já respondidas e aguarda o clique em Continuar.
  const [aguardandoContinuar, setAguardandoContinuar] = useState(false);
  // Filtro "Ir para Pergunta": por número da pergunta ou por nome (autocomplete).
  const [navMode, setNavMode] = useState<'numero' | 'nome'>('numero');
  const [navNumero, setNavNumero] = useState('');
  // Equivalência: respostas herdadas de outro questionário (qp_id → nome de origem).
  const [respostaHerdada, setRespostaHerdada] = useState<Record<number, string>>({});
  const [herdadasConfirmadas, setHerdadasConfirmadas] = useState<Set<number>>(new Set());
  const [herdadaModal, setHerdadaModal] = useState<{ qpId: number; novoValor: any } | null>(null);
  const [showChecklistFor, setShowChecklistFor] = useState<number | null>(null);
  const [checklistShown, setChecklistShown] = useState<Set<number>>(new Set());
  const [pendingCascade, setPendingCascade] = useState<{ qpId: number; value: any } | null>(null);
  const [isFinished, setIsFinished] = useState(false);

  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Carrega as opções do combobox de Classificação de Conformidade (Cadastro Auxiliar
  // global) uma única vez, só quando a tela abre em modo Auditoria.
  useEffect(() => {
    if (!modoAuditoria) return;
    api.getCadastrosAuxiliares('classificacao_conformidade').then(res => {
      if (res.success && Array.isArray(res.data)) {
        setClassificacoesConformidade((res.data as any[]).map(c => ({ key: String(c.id), label: c.nome })));
      }
    });
  }, [modoAuditoria]);

  // Atualiza um campo de auditoria só em memória (persistência é explícita, via persistAuditoria).
  const updateAuditoriaField = (qpId: number, patch: Partial<AuditoriaItem>) => {
    setAuditoriaDados(prev => ({ ...prev, [qpId]: { ...(prev[qpId] || AuditoriaItemVazio), ...patch } }));
  };

  // Handlers de Auditoria são isolados de propósito: NÃO reaproveitam nenhum handler
  // existente que suprima escrita quando `isFechado` (ex. persistChecklistDetalhe) — o
  // modo Auditoria precisa escrever justamente quando o questionário está fechado.
  const persistAuditoria = async (qpId: number, overrides?: Partial<AuditoriaItem>) => {
    const atual = { ...(auditoriaDados[qpId] || AuditoriaItemVazio), ...(overrides || {}) };
    try {
      await api.salvarAuditoria({
        questionario_id: Number(id),
        questionario_pergunta_id: qpId,
        classificacao_conformidade_id: atual.classificacao_conformidade_id,
        comentario_auditor: atual.comentario_auditor,
      });
    } catch {
      notify.error('Erro ao salvar a auditoria.');
    }
  };

  const handleAuditoriaAnexoUpload = async (qpId: number, files: FileList | null) => {
    if (!files || files.length === 0) return;
    setAuditoriaUploading(true);
    try {
      const res = await api.uploadAuditoriaAnexo(
        { questionario_id: Number(id), questionario_pergunta_id: qpId },
        Array.from(files)
      );
      if (res.success && Array.isArray(res.data)) {
        const atual = auditoriaDados[qpId] || AuditoriaItemVazio;
        updateAuditoriaField(qpId, { anexos: [...atual.anexos, ...(res.data as any[])] });
      } else {
        notify.error('Erro ao anexar arquivo.');
      }
    } finally {
      setAuditoriaUploading(false);
    }
  };

  const handleAuditoriaAnexoRemover = async (qpId: number, anexoId: number) => {
    const res = await api.deleteAuditoriaAnexo(anexoId);
    if (res.success) {
      const atual = auditoriaDados[qpId] || AuditoriaItemVazio;
      updateAuditoriaField(qpId, { anexos: atual.anexos.filter(a => a.id !== anexoId) });
    } else {
      notify.error('Erro ao remover anexo.');
    }
  };

  const handleAuditoriaAnexoAbrir = async (anexoId: number) => {
    const res = await api.getAuditoriaAnexoUrl(anexoId);
    if (res.success && res.data?.url) window.open(res.data.url, '_blank');
    else notify.error('Erro ao abrir anexo.');
  };

  // ── Load questionnaire ──────────────────────────────────────────────────────

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      setLoading(true);
      try {
        const qRes = await api.getQuestionnaire(Number(id));

        if (!qRes.success || !qRes.data) {
          setError('Questionário não encontrado.');
          return;
        }

        const q = qRes.data;
        setQuestionnaire(q);

        // Sempre sem usuarioId: o questionário é um documento coletivo da empresa, não uma
        // cópia pessoal de quem está logado — qualquer usuário autorizado da empresa precisa
        // ver o progresso real (quem respondeu o quê), não só o que ele mesmo respondeu. Bug
        // relatado: Silvio via "0 de 57" num questionário que outro colega da mesma empresa
        // já tinha avançado, porque antes isso só ignorava usuario_id em Modo Auditoria ou
        // questionário fechado — nunca durante o preenchimento colaborativo normal.
        const aRes = await api.getAnswers(Number(id), undefined);

        const qps: QP[] = ((q.perguntas || []) as QP[]).sort((a, b) => (a.ordem || 0) - (b.ordem || 0));
        setPerguntas(qps);

        const pMap = new Map<number, QP>(qps.map(p => [p.qp_id, p]));
        setPerguntasMap(pMap);

        const pList = qps.filter(p => p.nivel === 'principal');
        setPrincipals(pList);

        // Carrega respostas existentes (chave = questionario_pergunta_id)
        if (aRes.success && Array.isArray(aRes.data)) {
          const existing: Record<number, any> = {};
          const existingChecklist: Record<number, Record<number, ChecklistItemResposta>> = {};
          const existingDetalhes: Record<number, ChecklistItemDetalhe> = {};
          const existingChecklistDetalhes: Record<number, Record<number, ChecklistItemDetalhe>> = {};
          const existingHerdada: Record<number, string> = {};
          const existingAuditoria: Record<number, AuditoriaItem> = {};
          (aRes.data as any[]).forEach(r => {
            if (modoAuditoria) {
              existingAuditoria[r.questionario_pergunta_id] = {
                classificacao_conformidade_id: r.classificacao_conformidade_id ?? null,
                comentario_auditor: r.comentario_auditor ?? null,
                anexos: r.auditoria_anexos || [],
              };
            }
            const v = r.resposta_texto ?? r.resposta_percentual;
            if (v !== undefined && v !== null) {
              existing[r.questionario_pergunta_id] = v;
            }
            // Resposta herdada por equivalência de outro questionário.
            if (r.herdada_de_questionario_id) {
              existingHerdada[r.questionario_pergunta_id] = r.herdada_origem_nome || 'outro questionário';
            }
            // Detalhe de Nível de Controle (Plano/Justificativa/Evidência) na própria resposta.
            if (r.justificativa || r.plano_data_limite || r.plano_responsavel_nome
              || r.plano_responsavel_email || r.plano_descricao || r.evidencia_nome || r.evidencia_comentario) {
              existingDetalhes[r.questionario_pergunta_id] = {
                justificativa: r.justificativa ?? null,
                plano_data_limite: r.plano_data_limite ? String(r.plano_data_limite).slice(0, 10) : null,
                plano_responsavel_nome: r.plano_responsavel_nome ?? null,
                plano_responsavel_email: r.plano_responsavel_email ?? null,
                plano_descricao: r.plano_descricao ?? null,
                evidencia_nome: r.evidencia_nome ?? null,
                evidencia_comentario: r.evidencia_comentario ?? null,
              };
            }
            // checklist_respostas: item_id → { resposta, justificativa, plano_*, evidencia_nome }
            if (r.checklist_respostas && Object.keys(r.checklist_respostas).length > 0) {
              const respMap: Record<number, ChecklistItemResposta> = {};
              const detMap: Record<number, ChecklistItemDetalhe> = {};
              Object.entries(r.checklist_respostas as Record<string, any>).forEach(([itemId, det]) => {
                if (!det) return;
                if (det.resposta) respMap[Number(itemId)] = det.resposta;
                if (det.justificativa || det.plano_data_limite || det.plano_responsavel_nome
                  || det.plano_responsavel_email || det.plano_descricao || det.evidencia_nome || det.evidencia_comentario) {
                  detMap[Number(itemId)] = {
                    justificativa: det.justificativa ?? null,
                    plano_data_limite: det.plano_data_limite ? String(det.plano_data_limite).slice(0, 10) : null,
                    plano_responsavel_nome: det.plano_responsavel_nome ?? null,
                    plano_responsavel_email: det.plano_responsavel_email ?? null,
                    plano_descricao: det.plano_descricao ?? null,
                    evidencia_nome: det.evidencia_nome ?? null,
                    evidencia_comentario: det.evidencia_comentario ?? null,
                  };
                }
              });
              existingChecklist[r.questionario_pergunta_id] = respMap;
              if (Object.keys(detMap).length > 0) existingChecklistDetalhes[r.questionario_pergunta_id] = detMap;
            }
          });
          setAnswers(existing);
          setChecklistAnswers(existingChecklist);
          setChecklistDetalhes(existingChecklistDetalhes);
          setRespostaDetalhes(existingDetalhes);
          setRespostaHerdada(existingHerdada);
          if (modoAuditoria) setAuditoriaDados(existingAuditoria);

          // Retoma na próxima Pergunta a responder (1º principal sem resposta). Se todas
          // já respondidas (finalizado, mas não fechado), abre na última respondida —
          // sem disparar a finalização automática. Não se aplica em modoAuditoria: o
          // auditor sempre começa do primeiro bloco, revisando tudo em ordem.
          if (!modoAuditoria) {
            const proximaIdx = pList.findIndex(p => existing[p.qp_id] === undefined);
            if (proximaIdx > 0) {
              setPrincipalIndex(proximaIdx);
            } else if (proximaIdx === -1 && pList.length > 0) {
              setPrincipalIndex(pList.length - 1);
            }
          }
        }

        // Carrega antecipadamente a definição de todos os checklists do questionário,
        // para que o cálculo de maturidade funcione mesmo ao retomar uma sessão anterior.
        const qpsComChecklist = qps.filter(p => p.checklist_id);
        if (qpsComChecklist.length > 0) {
          const results = await Promise.all(
            qpsComChecklist.map(p => api.getChecklistCompleto(p.checklist_id!))
          );
          const newChecklistData: Record<number, ChecklistRequisito[]> = {};
          qpsComChecklist.forEach((p, i) => {
            const r = results[i];
            if (r.success && r.data) newChecklistData[p.qp_id] = (r.data as any).requisitos || [];
          });
          setChecklistData(prev => ({ ...prev, ...newChecklistData }));

          // Corrige, de forma determinística, a exibição do checklist da pergunta em
          // que a sessão está retomando: o efeito de recálculo de bloco roda antes
          // deste fetch terminar (o `await` acima cede a vez ao React) e nesse momento
          // checklistData ainda está vazio — então a decisão de mostrar o checklist
          // dependia de uma corrida com a rede, não do estado real dos dados. Aqui já
          // temos os requisitos e as respostas carregadas, então recalculamos direto.
          // Em modoAuditoria sempre pousamos no primeiro bloco (ver acima), então a
          // pergunta de "pouso" pra pré-expandir o checklist é sempre a primeira Principal.
          const landingQp = modoAuditoria
            ? (pList.length > 0 ? pList[0] : null)
            : (aRes.success && Array.isArray(aRes.data)
              ? (() => {
                  const existing: Record<number, any> = {};
                  (aRes.data as any[]).forEach(r => {
                    const v = r.resposta_texto ?? r.resposta_percentual;
                    if (v !== undefined && v !== null) existing[r.questionario_pergunta_id] = v;
                  });
                  const proximaIdx = pList.findIndex(p => existing[p.qp_id] === undefined);
                  if (proximaIdx > 0) return pList[proximaIdx];
                  if (proximaIdx === -1 && pList.length > 0) return pList[pList.length - 1];
                  return null;
                })()
              : null);

          if (landingQp && landingQp.checklist_id && newChecklistData[landingQp.qp_id]) {
            const pAnsLanding = (aRes.data as any[]).find(r => r.questionario_pergunta_id === landingQp.qp_id);
            const valorLanding = pAnsLanding ? (pAnsLanding.resposta_texto ?? pAnsLanding.resposta_percentual) : undefined;
            if (valorLanding === 'SIM') {
              const respMap: Record<number, ChecklistItemResposta> = {};
              if (pAnsLanding?.checklist_respostas) {
                Object.entries(pAnsLanding.checklist_respostas as Record<string, any>).forEach(([itemId, det]) => {
                  if (det?.resposta) respMap[Number(itemId)] = det.resposta;
                });
              }
              const requisitos = newChecklistData[landingQp.qp_id];
              const completo = requisitos.every(req => req.itens.every(item => item.ativo === false || !!respMap[item.id]));
              if (!completo) {
                setVisibleExtra([]);
                setShowChecklistFor(landingQp.qp_id);
                setChecklistShown(prev => new Set(prev).add(landingQp.qp_id));
                setPendingCascade({ qpId: landingQp.qp_id, value: 'SIM' });
              }
            }
          }
        }
      } catch {
        setError('Erro ao carregar questionário.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, usuarioId, modoAuditoria]);

  // ── Load checklist when needed ──────────────────────────────────────────────

  const loadChecklist = useCallback(async (qpId: number) => {
    const qp = perguntasMap.get(qpId);
    if (!qp || !qp.checklist_id || checklistData[qpId]) return;
    try {
      const res = await api.getChecklistCompleto(qp.checklist_id);
      if (res.success && res.data) {
        setChecklistData(prev => ({ ...prev, [qpId]: (res.data as any).requisitos || [] }));
      }
    } catch { /* checklist não disponível */ }
  }, [perguntasMap, checklistData]);

  // ── Cascade / navigation helpers ────────────────────────────────────────────

  // Segue a cadeia de perguntas complementares ligadas pela condição SIM/RESPONDIDA
  // a partir de uma pergunta (exclusive), retornando os qp_ids seguintes da cadeia.
  const getComplementarChainFrom = useCallback((startQpId: number): number[] => {
    const chain: number[] = [];
    let cur = startQpId;
    const visited = new Set<number>();
    while (!visited.has(cur)) {
      visited.add(cur);
      const qp = perguntasMap.get(cur);
      if (!qp) break;
      const cond = (qp.condicoes || [])
        .filter(c => c.resposta_valor === 'SIM' || c.resposta_valor === 'RESPONDIDA')
        .sort((a, b) => (a.ordem || 0) - (b.ordem || 0))
        .find(c => c.acao === 'COMPLEMENTAR' && c.pergunta_complementar_qp_id);
      if (!cond || !cond.pergunta_complementar_qp_id) break;
      chain.push(cond.pergunta_complementar_qp_id);
      cur = cond.pergunta_complementar_qp_id;
    }
    return chain;
  }, [perguntasMap]);

  // Checklist é automático: não depende de Condições configuradas — basta a pergunta
  // ter Checklist habilitado e a resposta ser SIM. Para NÃO e N/A o checklist não aparece.
  const shouldShowChecklist = useCallback((qpId: number, answerValue: string): boolean => {
    const qp = perguntasMap.get(qpId);
    return !!qp?.checklist_id && answerValue === 'SIM';
  }, [perguntasMap]);

  const goToNextPrincipal = useCallback(() => {
    setVisibleExtra([]);
    setAguardandoContinuar(false);
    setPrincipalIndex(i => {
      if (i + 1 >= principals.length) {
        setIsFinished(true);
        return i;
      }
      return i + 1;
    });
  }, [principals]);

  // ── Answer handlers ─────────────────────────────────────────────────────────

  const saveAnswerValue = useCallback((qpId: number, value: any, cascaded: boolean) => {
    setAnswers(prev => ({ ...prev, [qpId]: value }));
    setCascadedIds(prev => {
      const next = new Set(prev);
      if (cascaded) next.add(qpId); else next.delete(qpId);
      return next;
    });
    // Salvamento automático: cada resposta é persistida assim que é definida.
    api.saveAnswer({
      questionario_id: Number(id),
      pergunta_id: qpId,
      usuario_id: usuarioId,
      resposta: String(value),
      porcentagem: typeof value === 'number' ? value : null,
    }).catch(() => { /* falha silenciosa; próxima interação tenta novamente */ });
  }, [id, usuarioId]);

  const proceedAfterAnswer = useCallback((qpId: number, value: any) => {
    const qp = perguntasMap.get(qpId);
    if (!qp || qp.tipo_resposta !== 'SIM_NAO') return; // demais tipos avançam via botão "Continuar"

    setAguardandoContinuar(false); // religa abaixo apenas no caso NÃO/N/A com cascata
    const chain = getComplementarChainFrom(qpId);

    // Regra 1: Principal = NÃO ou N/A cascateia para todas as Complementares da cadeia.
    // Regra 2: Complementar = NÃO cascateia NÃO para as Complementares restantes da cadeia.
    // Regra 3: Complementar = N/A NÃO cascateia — segue a regra do SIM (revela a próxima).
    // Em vez de pular direto, mostra essas perguntas já respondidas e aguarda o clique
    // em "Continuar questionário". (Regra vale para Perguntas; não para itens de checklist.)
    const cascateia = value === 'NAO' || (value === 'NA' && qp.nivel === 'principal');
    if (cascateia) {
      if (chain.length === 0) {
        goToNextPrincipal();
      } else {
        chain.forEach(cid => saveAnswerValue(cid, value, true));
        setVisibleExtra(prev => Array.from(new Set([...prev, ...chain])));
        setAguardandoContinuar(true);
      }
      return;
    }

    // SIM (ou N/A numa Complementar): revela a próxima Complementar da cadeia (uma a uma).
    // Se a cadeia à frente ainda carrega respostas assumidas por uma cascata anterior
    // (ex.: a pergunta foi respondida NÃO/N/A, cascateou para as Complementares, e depois
    // foi alterada para SIM sem clicar em "Continuar questionário"), essas respostas
    // ficam obsoletas — limpa para o usuário responder de novo. Sem isso, as Complementares
    // permaneciam travadas (isLocked) mostrando o valor antigo do "por cascata".
    const cascadedInChain = chain.filter(cid => cascadedIds.has(cid));
    if (cascadedInChain.length > 0) {
      setAnswers(prev => {
        const next = { ...prev };
        cascadedInChain.forEach(cid => delete next[cid]);
        return next;
      });
      setCascadedIds(prev => {
        const next = new Set(prev);
        cascadedInChain.forEach(cid => next.delete(cid));
        return next;
      });
    }

    if (qp.nivel === 'principal') {
      if (chain.length === 0) {
        goToNextPrincipal();
      } else {
        setVisibleExtra([chain[0]]);
      }
    } else {
      if (chain.length === 0) {
        goToNextPrincipal();
      } else {
        setVisibleExtra(prev => (prev.includes(chain[0]) ? prev : [...prev, chain[0]]));
      }
    }
  }, [perguntasMap, getComplementarChainFrom, goToNextPrincipal, saveAnswerValue, cascadedIds]);

  const handleAnswerSelect = useCallback(async (qpId: number, value: any) => {
    const qp = perguntasMap.get(qpId);
    if (!qp) return;

    // Clique no mesmo valor já respondido (ex.: usuário voltou para conferir uma pergunta
    // já respondida do bloco): não reprocessa a cascata/revelação, para não descartar o
    // progresso já feito nas Complementares seguintes.
    if (qp.tipo_resposta === 'SIM_NAO' && answers[qpId] === value) return;

    // Resposta herdada por equivalência: não pode ser alterada só neste questionário.
    // Se o usuário escolher um valor diferente, confirma antes se altera em todos os ativos.
    if (respostaHerdada[qpId] && !herdadasConfirmadas.has(qpId) && String(answers[qpId]) !== String(value)) {
      setHerdadaModal({ qpId, novoValor: value });
      return;
    }

    saveAnswerValue(qpId, value, false);

    const valueStr = String(value);
    if (shouldShowChecklist(qpId, valueStr) && !checklistShown.has(qpId)) {
      await loadChecklist(qpId);
      setShowChecklistFor(qpId);
      setChecklistShown(prev => new Set(prev).add(qpId));
      setPendingCascade({ qpId, value });
      return;
    }

    // Nível de Controle: quando a resposta exige Plano/Evidência/Justificativa, mantém
    // a pergunta visível com os campos inline e adia o avanço até o usuário clicar Continuar.
    if (qp.usa_nivel_controle && detalheExigido(qp.nivel_controle, valueStr as ChecklistItemResposta)) {
      setPendingNivel({ qpId, value });
      return;
    }

    proceedAfterAnswer(qpId, value);
  }, [perguntasMap, saveAnswerValue, shouldShowChecklist, checklistShown, loadChecklist, proceedAfterAnswer, respostaHerdada, herdadasConfirmadas, answers]);

  // Confirmação de alteração de uma resposta herdada: aplica em todos os ativos ou cancela.
  const confirmarAlteracaoHerdada = (alterar: boolean) => {
    if (!herdadaModal) return;
    const { qpId, novoValor } = herdadaModal;
    if (alterar) {
      // Deixa de ser herdada (vira principal) e propaga aos demais via backend.
      setRespostaHerdada(prev => { const n = { ...prev }; delete n[qpId]; return n; });
      setHerdadasConfirmadas(prev => new Set(prev).add(qpId));
      setHerdadaModal(null);
      handleAnswerSelect(qpId, novoValor);
    } else {
      setHerdadaModal(null);
    }
  };

  // Confirma a resposta herdada (sem alterá-la) e avança para a próxima pergunta.
  // Replica a checagem de Checklist que handleAnswerSelect já faz — sem isso, uma
  // pergunta com Checklist cuja resposta veio por herança nunca chegava a mostrar
  // os itens ao clicar aqui (só aparecia indiretamente ao clicar em Voltar).
  // Não checa `checklistShown` aqui (diferente de handleAnswerSelect): este botão só
  // reconfirma uma resposta já existente, sem receber um valor novo, então não há
  // clique redundante a proteger — e checklistShown, por nunca ser limpo ao navegar,
  // bloqueava a reabertura legítima do checklist ao Voltar pra esta pergunta e clicar
  // em Confirmar e Avançar de novo.
  const confirmarHerdadaEAvancar = async (qpId: number) => {
    setHerdadasConfirmadas(prev => new Set(prev).add(qpId));
    const value = answers[qpId];
    const valueStr = String(value);
    if (shouldShowChecklist(qpId, valueStr)) {
      await loadChecklist(qpId);
      setShowChecklistFor(qpId);
      setChecklistShown(prev => new Set(prev).add(qpId));
      setPendingCascade({ qpId, value });
      return;
    }
    proceedAfterAnswer(qpId, value);
  };

  const handleChecklistConfirm = useCallback(() => {
    setShowChecklistFor(null);
    if (pendingCascade) {
      proceedAfterAnswer(pendingCascade.qpId, pendingCascade.value);
      setPendingCascade(null);
    }
  }, [pendingCascade, proceedAfterAnswer]);

  const handleChecklistAnswer = (qpId: number, itemId: number, resposta: ChecklistItemResposta, nivelControle?: NivelControle) => {
    setChecklistAnswers(prev => ({
      ...prev,
      [qpId]: { ...(prev[qpId] || {}), [itemId]: resposta },
    }));
    const det = (checklistDetalhes[qpId] || {})[itemId] || {};
    api.saveChecklistItemResposta({
      questionario_id: Number(id),
      pergunta_id: qpId,
      usuario_id: usuarioId,
      item_id: itemId,
      resposta,
      justificativa: det.justificativa ?? null,
      plano_data_limite: det.plano_data_limite ?? null,
      plano_responsavel_nome: det.plano_responsavel_nome ?? null,
      plano_responsavel_email: det.plano_responsavel_email ?? null,
    }).catch(() => { /* falha silenciosa; próxima marcação tenta novamente */ });

    // Nível de Controle no item: quando a resposta exige Plano/Evidência/Justificativa,
    // abre o pop-up de detalhe imediatamente (espaço limitado na linha da tabela).
    const tipo = detalheExigido(nivelControle, resposta);
    if (tipo) setItemDetalheModal({ qpId, itemId, tipo });
  };

  // "Marcar todos": aplica a mesma resposta (Sim/Não/N/A) a todos os itens ativos de um
  // requisito de uma vez. Não abre o pop-up de Plano/Justificativa/Evidência (abriria um
  // por item, sobrepondo os anteriores) — o item continua mostrando o aviso colorido pra
  // ser preenchido individualmente depois, exatamente como se tivesse sido marcado um a um.
  const handleMarcarTodosChecklist = (qpId: number, itens: ChecklistItem[], resposta: ChecklistItemResposta) => {
    const ativos = itens.filter(item => item.ativo !== false);
    if (ativos.length === 0) return;
    setChecklistAnswers(prev => ({
      ...prev,
      [qpId]: { ...(prev[qpId] || {}), ...Object.fromEntries(ativos.map(item => [item.id, resposta])) },
    }));
    ativos.forEach(item => {
      const det = (checklistDetalhes[qpId] || {})[item.id] || {};
      api.saveChecklistItemResposta({
        questionario_id: Number(id),
        pergunta_id: qpId,
        usuario_id: usuarioId,
        item_id: item.id,
        resposta,
        justificativa: det.justificativa ?? null,
        plano_data_limite: det.plano_data_limite ?? null,
        plano_responsavel_nome: det.plano_responsavel_nome ?? null,
        plano_responsavel_email: det.plano_responsavel_email ?? null,
        plano_descricao: det.plano_descricao ?? null,
      }).catch(() => { /* falha silenciosa; próxima marcação tenta novamente */ });
    });
  };

  // Atualiza um campo de detalhe da resposta de um ITEM do checklist (estado local).
  const updateChecklistDetalhe = (qpId: number, itemId: number, patch: Partial<ChecklistItemDetalhe>) => {
    setChecklistDetalhes(prev => ({
      ...prev,
      [qpId]: { ...(prev[qpId] || {}), [itemId]: { ...((prev[qpId] || {})[itemId] || {}), ...patch } },
    }));
  };

  // Persiste Plano/Justificativa do item junto da marcação já salva (resposta SIM/NAO/NA).
  const persistChecklistDetalhe = (qpId: number, itemId: number) => {
    const resposta = (checklistAnswers[qpId] || {})[itemId];
    if (!resposta) return;
    const det = (checklistDetalhes[qpId] || {})[itemId] || {};
    api.saveChecklistItemResposta({
      questionario_id: Number(id),
      pergunta_id: qpId,
      usuario_id: usuarioId,
      item_id: itemId,
      resposta,
      justificativa: det.justificativa ?? null,
      plano_data_limite: det.plano_data_limite ?? null,
      plano_responsavel_nome: det.plano_responsavel_nome ?? null,
      plano_responsavel_email: det.plano_responsavel_email ?? null,
      plano_descricao: det.plano_descricao ?? null,
      evidencia_comentario: det.evidencia_comentario ?? null,
    }).catch(() => { /* falha silenciosa */ });
  };

  const handleChecklistItemEvidenciaUpload = async (qpId: number, itemId: number, file: File | null) => {
    if (!file) return;
    setEvidenciaSavingItem(itemId);
    try {
      const up = await api.uploadChecklistEvidencia(
        { questionario_id: Number(id), pergunta_id: qpId, usuario_id: usuarioId, item_id: itemId }, file);
      if (!up.success) throw new Error('Erro ao enviar a evidência.');
      updateChecklistDetalhe(qpId, itemId, { evidencia_nome: (up.data as any)?.evidencia_nome || file.name });
    } catch (err: any) {
      notify.error(err?.message || 'Erro ao enviar evidência.');
    } finally {
      setEvidenciaSavingItem(null);
    }
  };

  // Todos os itens (ativos) do checklist precisam ter resposta (SIM/NÃO/N/A) antes de avançar.
  const isChecklistComplete = (qpId: number): boolean => {
    const requisitos = checklistData[qpId];
    if (!requisitos) return false;
    const respostasItem = checklistAnswers[qpId] || {};
    return requisitos.every(req => req.itens.every(item => item.ativo === false || !!respostasItem[item.id]));
  };

  // ── Nível de Controle (Plano de Ação / Evidência / Justificativa na PERGUNTA) ──

  // Atualiza um campo de detalhe da resposta da pergunta (estado local).
  const updateRespostaDetalhe = (qpId: number, patch: Partial<ChecklistItemDetalhe>) => {
    setRespostaDetalhes(prev => ({ ...prev, [qpId]: { ...(prev[qpId] || {}), ...patch } }));
  };

  // Persiste Plano/Justificativa (texto/data) junto da resposta da pergunta.
  const persistRespostaDetalhe = (qpId: number) => {
    const det = respostaDetalhes[qpId] || {};
    api.saveAnswer({
      questionario_id: Number(id),
      pergunta_id: qpId,
      usuario_id: usuarioId,
      resposta: answers[qpId] != null ? String(answers[qpId]) : undefined,
      justificativa: det.justificativa ?? null,
      plano_data_limite: det.plano_data_limite ?? null,
      plano_responsavel_nome: det.plano_responsavel_nome ?? null,
      plano_responsavel_email: det.plano_responsavel_email ?? null,
      plano_descricao: det.plano_descricao ?? null,
      evidencia_comentario: det.evidencia_comentario ?? null,
    }).catch(() => { /* falha silenciosa */ });
  };

  const handleEvidenciaUpload = async (qpId: number, file: File | null) => {
    if (!file) return;
    setEvidenciaSaving(qpId);
    try {
      const up = await api.uploadRespostaEvidencia(
        { questionario_id: Number(id), pergunta_id: qpId, usuario_id: usuarioId }, file);
      if (!up.success) throw new Error('Erro ao enviar a evidência.');
      updateRespostaDetalhe(qpId, { evidencia_nome: (up.data as any)?.evidencia_nome || file.name });
    } catch (err: any) {
      notify.error(err?.message || 'Erro ao enviar evidência.');
    } finally {
      setEvidenciaSaving(null);
    }
  };

  // Abre o arquivo de evidência já anexado (pergunta principal ou complementar). Nunca
  // manda usuario_id — quem está vendo pode não ser quem respondeu/anexou (questionário
  // é documento coletivo da empresa, ver comentário no carregamento das respostas).
  const handleAbrirEvidenciaResposta = async (qpId: number) => {
    const res = await api.getRespostaEvidenciaUrl({
      questionario_id: Number(id), pergunta_id: qpId, usuario_id: undefined,
    });
    if (res.success && res.data?.url) window.open(res.data.url, '_blank');
    else notify.error('Erro ao abrir evidência.');
  };

  const handleAbrirEvidenciaChecklistItem = async (qpId: number, itemId: number) => {
    const res = await api.getChecklistEvidenciaUrl({
      questionario_id: Number(id), pergunta_id: qpId, item_id: itemId, usuario_id: undefined,
    });
    if (res.success && res.data?.url) window.open(res.data.url, '_blank');
    else notify.error('Erro ao abrir evidência.');
  };

  // Pede uma sugestão de texto pro Plano de Ação via IA — só preenche o campo, nunca
  // salva sozinho: o usuário ainda revisa/edita e confirma como sempre.
  const handleSugerirPlano = async (qpId: number) => {
    const qp = perguntasMap.get(qpId);
    if (!qp) return;
    setSugerindoPlano(qpId);
    try {
      const res = await api.sugerirPlanoDeAcao({
        pergunta_texto: qp.pergunta_text,
        dominio_nome: qp.controle_nome,
        framework_nome: questionnaire?.framework,
        controle_indice: qp.controle_indice,
      });
      if (!res.success) throw new Error((res.data as any)?.error || 'Erro ao gerar sugestão.');
      updateRespostaDetalhe(qpId, { plano_descricao: (res.data as any)?.sugestao || '' });
    } catch (err: any) {
      notify.error(err?.message || 'Erro ao gerar sugestão. Tente novamente ou escreva manualmente.');
    } finally {
      setSugerindoPlano(null);
    }
  };

  const handleNivelContinue = () => {
    if (!pendingNivel) return;
    persistRespostaDetalhe(pendingNivel.qpId);
    proceedAfterAnswer(pendingNivel.qpId, pendingNivel.value);
    setPendingNivel(null);
  };

  const handleBack = () => {
    if (isFinished) {
      setIsFinished(false);
      return;
    }
    if (principalIndex === 0) return;
    setShowChecklistFor(null);
    setPrincipalIndex(i => Math.max(0, i - 1));
  };

  // Navega direto para uma Pergunta principal (0-based, índice dentro de `principals`).
  const irParaPergunta = (index: number) => {
    if (index < 0 || index >= principals.length) return;
    if (isFinished) setIsFinished(false);
    setShowChecklistFor(null);
    setPrincipalIndex(index);
  };

  // Botão "Avançar →": navegação livre para a próxima Pergunta principal, equivalente a usar
  // o Filtro "Ir para Pergunta" com o número seguinte — não exige resposta nem checklist
  // concluído (mesma liberdade que o Filtro já tinha). Na última pergunta, leva direto para a
  // tela de conclusão em vez de tentar avançar para um índice inexistente.
  const handleAvancar = () => {
    if (principalIndex >= principals.length - 1) {
      setIsFinished(true);
    } else {
      irParaPergunta(principalIndex + 1);
    }
  };

  // Navega a partir do número GLOBAL da pergunta (1-based, mesma escala do badge
  // "Pergunta X de Y" e do "Progresso: X de Y" — conta Principais e Complementares).
  // Como a navegação só pousa em blocos de Principal, um número que cai numa
  // Complementar leva ao bloco da Principal correspondente; o efeito de recomposição do
  // bloco já revela a cadeia até a última resposta existente, então se a Complementar
  // já tiver sido respondida ela aparece normalmente ao chegar no bloco.
  const irParaPerguntaGlobal = (numeroGlobal: number) => {
    const alvo = perguntas[numeroGlobal - 1];
    if (!alvo) return;
    const idx = alvo.nivel === 'principal'
      ? principals.findIndex(p => p.qp_id === alvo.qp_id)
      : principals.findIndex(p => getComplementarChainFrom(p.qp_id).includes(alvo.qp_id));
    if (idx >= 0) irParaPergunta(idx);
  };

  // SM-006: o mesmo "mantém visível pra revisão" de cima (linha ~1284) só cobria a
  // Principal — cada Complementar revelada tem seu próprio checklist independente, que
  // precisa do mesmo tratamento (sem isso, só aparecia depois de Avançar e Voltar, porque
  // só os fluxos interativos de resposta marcavam checklistShown, nunca a recomposição
  // do bloco ao carregar/retomar).
  const marcarChecklistsReveladosComoVistos = useCallback((qpIds: number[]) => {
    const comChecklist = qpIds.filter(cid => shouldShowChecklist(cid, String(answers[cid])));
    if (comChecklist.length === 0) return;
    comChecklist.forEach(loadChecklist);
    setChecklistShown(prev => {
      const next = new Set(prev);
      comChecklist.forEach(cid => next.add(cid));
      return next;
    });
  }, [answers, shouldShowChecklist, loadChecklist]);

  // Recompõe o bloco visível ao trocar de Principal (avançar/voltar/retomar),
  // a partir das respostas já salvas — não reage a cada resposta individual.
  useEffect(() => {
    const principal = principals[principalIndex];
    if (!principal) return;
    setAguardandoContinuar(false);
    const pAns = answers[principal.qp_id];

    if (pAns === undefined || principal.tipo_resposta !== 'SIM_NAO') {
      setVisibleExtra([]);
      setShowChecklistFor(null);
      return;
    }

    // Resposta herdada por equivalência ainda não confirmada: mostra só a pergunta
    // (respondida) com o aviso e o botão "Confirmar e Avançar"; não cascateia nem avança.
    // Não se aplica a questionário Fechado/Inativo: "confirmar" só existe como ação da
    // sessão em memória (nunca é persistido no banco), então ao reabrir pra revisão essa
    // herança sempre reaparecia como "pendente" — mesmo já tendo sido fechada há tempos,
    // escondendo tudo (inclusive o checklist, ver SM-001). Num questionário fechado nada
    // mais pode ser alterado mesmo, então não há confirmação real pendente: mostra
    // direto o estado final, igual a uma resposta própria.
    const isFechadoOuInativo = !!questionnaire?.is_fechado || !!questionnaire?.is_inativo;
    if (!isFechadoOuInativo && respostaHerdada[principal.qp_id] && !herdadasConfirmadas.has(principal.qp_id)) {
      setVisibleExtra([]);
      setShowChecklistFor(null);
      setAguardandoContinuar(false);
      return;
    }

    // Retomando uma sessão: se a Principal foi respondida SIM mas o checklist
    // ainda não foi concluído (ex.: página recarregada antes de confirmar),
    // reabre o checklist em vez de travar a pergunta sem dar nenhuma saída.
    if (pAns === 'SIM' && principal.checklist_id && !isChecklistComplete(principal.qp_id)) {
      setVisibleExtra([]);
      loadChecklist(principal.qp_id);
      setShowChecklistFor(principal.qp_id);
      setChecklistShown(prev => new Set(prev).add(principal.qp_id));
      setPendingCascade({ qpId: principal.qp_id, value: 'SIM' });
      return;
    }

    // Checklist já concluído: mantém visível pra revisão. Antes, ao navegar de volta pra
    // essa pergunta (Avançar/Voltar/"Ir para Pergunta"), o checklist ficava oculto mesmo
    // com o questionário ainda em andamento — não só quando fechado (bug relatado: o
    // checklist "desaparecia" de perguntas já respondidas). Não retorna aqui: o resto do
    // efeito (revelar a cadeia de Complementares já resolvida, por exemplo) segue rodando
    // normalmente — os controles do checklist já ficam desabilitados via isModelo/isFechado
    // mais abaixo, isso aqui só decide a EXIBIÇÃO.
    setShowChecklistFor(pAns === 'SIM' && principal.checklist_id ? principal.qp_id : null);
    // SM-005: checklistShown é quem decide a exibição (sempre visível, uma vez revelado);
    // showChecklistFor sozinho só controlava o botão de confirmar, não bastava mais.
    if (pAns === 'SIM' && principal.checklist_id) {
      setChecklistShown(prev => new Set(prev).add(principal.qp_id));
    }
    const chain = getComplementarChainFrom(principal.qp_id);
    if (pAns === 'SIM') {
      if (chain.length === 0) {
        // Bloco já totalmente resolvido (SIM sem complementares, checklist ok).
        // Não pula para a próxima Principal aqui: isso só faria sentido para saltar
        // páginas já respondidas ao retomar uma sessão, mas o retomo de sessão já
        // pousa direto na pergunta certa (proximaIdx, no load inicial) sem passar por
        // este efeito. Chegar aqui só acontece ao avançar interativamente
        // (Confirmar e Avançar) para uma pergunta já respondida — nesse caso o
        // usuário precisa VER a pergunta para poder confirmar/revisar, não pular
        // direto para a seguinte (era a causa do bug de "pular uma pergunta").
        setVisibleExtra([]);
        return;
      }
      const revealed: number[] = [];
      for (const cid of chain) {
        revealed.push(cid);
        if (answers[cid] === undefined) break;
      }
      setVisibleExtra(revealed);
      marcarChecklistsReveladosComoVistos(revealed);
    } else {
      // Principal respondida NÃO/N/A: mostra as complementares assumidas por cascata
      // e aguarda o clique em "Continuar questionário".
      setVisibleExtra(chain);
      setAguardandoContinuar(chain.length > 0);
      marcarChecklistsReveladosComoVistos(chain);
    }
    // checklistData ENTRA de propósito (SM-006): isChecklistComplete() depende dela
    // (retorna false enquanto não carregou), e o pré-carregamento de todos os checklists
    // do questionário roda em paralelo, depois dos sets de principals/answers. Sem reagir
    // a ela, a 1ª execução deste efeito podia achar um checklist já respondido como
    // "incompleto" (falso negativo por timing) e esconder a Complementar — corrigia
    // sozinho só ao navegar de novo (Avançar/Voltar), quando checklistData já tinha
    // chegado. As demais dependências continuam de fora de propósito (não reagir a cada
    // resposta individual).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [principalIndex, principals, getComplementarChainFrom, checklistData]);

  const handleFinish = () => {
    navigate('/app/questionarios/list');
  };

  // ── Render helpers ──────────────────────────────────────────────────────────

  const totalAnswered = Object.keys(answers).length;
  const progress = perguntas.length > 0 ? Math.round((totalAnswered / perguntas.length) * 100) : 0;

  const isModelo = !!questionnaire?.is_modelo;
  // Inativo tem o mesmo tratamento de somente-leitura que Fechado (a lista já direciona pro
  // ícone "Ver" nos dois casos) — evita que alguém edite respostas indo direto pela URL.
  const isFechado = !!questionnaire?.is_fechado || !!questionnaire?.is_inativo;

  const currentPrincipal = principals[principalIndex];
  const visibleChain: QP[] = currentPrincipal
    ? [currentPrincipal, ...visibleExtra.map(qid => perguntasMap.get(qid)).filter(Boolean) as QP[]]
    : [];

  const activeQp = visibleChain.length > 0 ? visibleChain[visibleChain.length - 1] : null;
  // Em modoAuditoria não há resposta "pendente" a aguardar — o questionário já foi
  // fechado, então a navegação nunca deve travar por falta de resposta do bloco atual
  // (um questionário pode fechar sem 100% respondido; ver SM-004).
  const blockAnswered = modoAuditoria ? true : (activeQp ? answers[activeQp.qp_id] !== undefined : false);
  const showContinueButton = showChecklistFor === null && (
    (!!activeQp && activeQp.tipo_resposta !== 'SIM_NAO') || aguardandoContinuar
  );

  // ── Renders ──────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
        <div className="mt-2 text-muted">Carregando questionário...</div>
      </Container>
    );
  }

  if (error || !questionnaire) {
    return (
      <Container className="py-4">
        <Alert variant="danger">{error || 'Questionário não encontrado.'}</Alert>
        <Button onClick={() => navigate('/app/questionarios/list')}>Voltar à lista</Button>
      </Container>
    );
  }

  if (isFinished) {
    return (
      <Container fluid className="py-3 py-md-4">
        <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
          <Button variant="outline-secondary" size="sm" onClick={handleBack}>← Voltar</Button>
          <h1 className="mb-0 fs-5">{questionnaire.nome}</h1>
        </div>
        <FinishScreen
          perguntas={perguntas}
          answers={answers}
          cascadedIds={cascadedIds}
          checklistAnswers={checklistAnswers}
          checklistData={checklistData}
          frameworkNome={questionnaire.framework}
          onBack={handleBack}
          onFinish={handleFinish}
        />
      </Container>
    );
  }

  return (
    <Container fluid className="py-3 py-md-4">
      {/* Header */}
      <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
        <Button variant="outline-secondary" size="sm" onClick={() => navigate('/app/questionarios/list')}>
          ← Lista
        </Button>
        <div>
          <h1 className="mb-0 fs-4">{questionnaire.nome}</h1>
          {questionnaire.descricao && <div className="text-muted small">{questionnaire.descricao}</div>}
        </div>
        {isModelo && <Badge bg="info" className="ms-auto">Modelo — somente leitura</Badge>}
        {isFechado && <Badge bg="success" className="ms-auto">Fechado</Badge>}
        {modoAuditoria && <Badge bg="primary" className={isModelo || isFechado ? '' : 'ms-auto'}>Modo Auditoria</Badge>}
      </div>

      {isModelo && (
        <Alert variant="warning" className="mb-3">
          Este é um questionário modelo e não pode receber respostas. Clone-o para criar uma instância.
        </Alert>
      )}
      {isFechado && (
        <Alert variant="info" className="mb-3">
          Este questionário está fechado. Clone-o para criar uma nova versão com as respostas.
        </Alert>
      )}

      {/* Progress */}
      <div className="mb-4">
        <div className="d-flex justify-content-between mb-1">
          <small className="text-muted">
            Progresso: {totalAnswered} de {perguntas.length} perguntas respondidas
          </small>
          <small className="text-muted">{progress}%</small>
        </div>
        <ProgressBar now={progress} variant="primary" style={{ height: 8 }} />
      </div>

      {/* Filtros para Perguntas: ir direto para uma Pergunta por número ou por nome */}
      {principals.length > 1 && (
        <div className="d-flex align-items-end gap-2 flex-wrap mb-3 p-2 border rounded bg-light">
          <div>
            <Form.Label className="small mb-1 fw-semibold">Ir para Pergunta</Form.Label>
            <Form.Select
              size="sm"
              value={navMode}
              onChange={e => { setNavMode(e.target.value as 'numero' | 'nome'); setNavNumero(''); }}
              style={{ minWidth: 150 }}
            >
              <option value="numero">Por número</option>
              <option value="nome">Por nome</option>
            </Form.Select>
          </div>
          {navMode === 'numero' ? (
            <div className="d-flex align-items-end gap-2">
              <Form.Control
                size="sm"
                type="number"
                min={1}
                max={perguntas.length}
                placeholder="Por número da pergunta no questionário"
                value={navNumero}
                onChange={e => setNavNumero(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const n = parseInt(navNumero, 10);
                    if (n >= 1 && n <= perguntas.length) irParaPerguntaGlobal(n);
                  }
                }}
                style={{ maxWidth: 340, minWidth: 240 }}
              />
              <Button
                size="sm"
                variant="outline-primary"
                onClick={() => {
                  const n = parseInt(navNumero, 10);
                  if (n >= 1 && n <= perguntas.length) irParaPerguntaGlobal(n);
                  else notify.warning(`Informe um número entre 1 e ${perguntas.length}.`);
                }}
              >
                Ir
              </Button>
            </div>
          ) : (
            <div style={{ minWidth: 280, flex: 1, maxWidth: 480 }}>
              <SearchSelectField
                placeholder="Digite o nome da pergunta..."
                options={perguntas.map((p, i): SearchOption => ({
                  key: String(i + 1),
                  label: `${i + 1}. ${p.pergunta_text}${p.nivel === 'complementar' ? ' (Complementar)' : ''}`,
                }))}
                value=""
                onSelect={opt => { if (opt) irParaPerguntaGlobal(parseInt(opt.key, 10)); }}
              />
            </div>
          )}
        </div>
      )}

      {/* Bloco de perguntas (Principal + Complementares reveladas) */}
      {visibleChain.map(qp => {
        // Travada apenas quando a resposta foi assumida por cascata (não é uma resposta
        // própria do usuário) — qualquer outra pergunta já respondida do bloco pode ser
        // alterada a qualquer momento, mesmo sem ser a "ativa" no topo da cadeia.
        const isLocked = cascadedIds.has(qp.qp_id);
        // Nível de Controle: qual detalhe (Plano/Evidência/Justificativa) a resposta exige.
        const respNivel = answers[qp.qp_id];
        const tipoNivel = qp.usa_nivel_controle
          ? detalheExigido(qp.nivel_controle, respNivel as ChecklistItemResposta)
          : null;
        const detNivel = respostaDetalhes[qp.qp_id] || {};
        const isPendingNivel = pendingNivel?.qpId === qp.qp_id;
        // Numeração sequencial da pergunta dentro do TOTAL do questionário (Principais +
        // Complementares), para bater com o contador "Progresso: X de Y perguntas".
        const numeroPergunta = perguntas.findIndex(p => p.qp_id === qp.qp_id) + 1;
        return (
          <Card className="mb-3 shadow-sm" key={qp.qp_id}>
            <Card.Header className="d-flex align-items-center gap-2">
              {qp.nivel === 'complementar' && <Badge bg="secondary">Complementar</Badge>}
              <Badge bg="primary">{`Pergunta ${numeroPergunta} de ${perguntas.length}`}</Badge>
            </Card.Header>
            <Card.Body>
              <p className="fs-5 mb-3" style={{ fontWeight: 500 }}>
                {qp.pergunta_text}
                {qp.controle_descricao && (
                  <OverlayTrigger
                    placement="bottom"
                    overlay={
                      <Tooltip id={`ctrl-info-${qp.qp_id}`} className="tooltip-controle-info">
                        <div className="text-start">{qp.controle_descricao}</div>
                      </Tooltip>
                    }
                  >
                    <span
                      className="ms-2 d-inline-flex align-items-center justify-content-center text-secondary align-middle"
                      style={{
                        cursor: 'help', width: 20, height: 20, borderRadius: '50%',
                        border: '1px solid #adb5bd', fontSize: '0.72rem', fontStyle: 'italic',
                        fontWeight: 700, userSelect: 'none', lineHeight: 1,
                      }}
                      aria-label="Informação do controle relacionado"
                    >i</span>
                  </OverlayTrigger>
                )}
              </p>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <AnswerInput
                  qp={qp}
                  value={answers[qp.qp_id]}
                  onChange={v => handleAnswerSelect(qp.qp_id, v)}
                  disabled={isModelo || isFechado || isLocked}
                />
                {cascadedIds.has(qp.qp_id) && (
                  <Badge bg="light" text="dark" className="border">
                    {answers[qp.qp_id] === 'NA' ? 'N/A' : 'NÃO'} por cascata
                  </Badge>
                )}
                {respostaHerdada[qp.qp_id] && (
                  <span className="text-warning small d-inline-flex align-items-center gap-1">
                    <span aria-hidden>⚠</span>
                    Resposta herdada automaticamente do questionário {respostaHerdada[qp.qp_id]}.
                  </span>
                )}
              </div>

              {/* Confirmar e Avançar quando a resposta veio herdada por equivalência.
                  Não checa herdadasConfirmadas aqui: ao voltar para a mesma pergunta já
                  confirmada, o botão precisa reaparecer para permitir avançar de novo. */}
              {respostaHerdada[qp.qp_id]
                && qp.qp_id === activeQp?.qp_id && !isModelo && !isFechado && (
                <Button
                  variant="primary"
                  size="sm"
                  className="mt-3"
                  onClick={() => confirmarHerdadaEAvancar(qp.qp_id)}
                  disabled={tipoNivel === 'PLANO' && !planoDescricaoValida(detNivel.plano_descricao)}
                >
                  Confirmar e Avançar
                </Button>
              )}

              {/* Nível de Controle: campos inline conforme nível + resposta */}
              {tipoNivel && (
                <div
                  className="mt-3 p-3 border rounded"
                  style={{
                    borderColor: tipoNivel === 'PLANO' ? '#dc3545' : tipoNivel === 'EVIDENCIA' ? '#198754' : '#6f42c1',
                    backgroundColor: tipoNivel === 'PLANO' ? '#fdf1f2' : tipoNivel === 'EVIDENCIA' ? '#f0f9f3' : '#f6f2fb',
                  }}
                >
                  {tipoNivel === 'PLANO' && (
                    <>
                      <div className="fw-semibold mb-2" style={{ color: '#dc3545' }}>Defina um Plano de Ação</div>
                      <Row className="g-2">
                        <Col md={4}>
                          <Form.Label className="small">Data limite</Form.Label>
                          <Form.Control
                            type="date"
                            value={detNivel.plano_data_limite || ''}
                            min={new Date().toISOString().slice(0, 10)}
                            onKeyDown={e => e.preventDefault()}
                            onChange={e => updateRespostaDetalhe(qp.qp_id, { plano_data_limite: e.target.value })}
                            onBlur={() => persistRespostaDetalhe(qp.qp_id)}
                            disabled={isModelo || isFechado}
                          />
                        </Col>
                        <Col md={4}>
                          <Form.Label className="small">Nome do Responsável</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Digite o nome completo"
                            value={detNivel.plano_responsavel_nome || ''}
                            onChange={e => updateRespostaDetalhe(qp.qp_id, { plano_responsavel_nome: e.target.value })}
                            onBlur={() => persistRespostaDetalhe(qp.qp_id)}
                            disabled={isModelo || isFechado}
                          />
                        </Col>
                        <Col md={4}>
                          <Form.Label className="small">E-mail</Form.Label>
                          <Form.Control
                            type="email"
                            placeholder="nome@empresa.com"
                            value={detNivel.plano_responsavel_email || ''}
                            isInvalid={!!detNivel.plano_responsavel_email && !emailValido(detNivel.plano_responsavel_email)}
                            onChange={e => updateRespostaDetalhe(qp.qp_id, { plano_responsavel_email: e.target.value })}
                            onBlur={() => persistRespostaDetalhe(qp.qp_id)}
                            disabled={isModelo || isFechado}
                          />
                          <Form.Control.Feedback type="invalid">E-mail inválido.</Form.Control.Feedback>
                        </Col>
                      </Row>
                      <Row className="g-2 mt-1">
                        <Col md={12}>
                          <div className="d-flex justify-content-between align-items-center">
                            <Form.Label className="small mb-1">Descrição do Plano de Ação (campo de texto livre)</Form.Label>
                            {!isModelo && !isFechado && (
                              <Button
                                variant="primary"
                                size="sm"
                                className="py-0 px-2 d-inline-flex align-items-center gap-1"
                                onClick={() => handleSugerirPlano(qp.qp_id)}
                                disabled={sugerindoPlano === qp.qp_id}
                              >
                                {sugerindoPlano === qp.qp_id ? (
                                  <>
                                    <Spinner as="span" animation="border" size="sm" />
                                    Sugerindo...
                                  </>
                                ) : (
                                  <>
                                    <span aria-hidden>✨</span>
                                    Sugerir com IA
                                  </>
                                )}
                              </Button>
                            )}
                          </div>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            maxLength={PLANO_DESCRICAO_MAX}
                            placeholder="Descreva as ações que serão tomadas..."
                            value={detNivel.plano_descricao || ''}
                            isInvalid={!!detNivel.plano_descricao && !planoDescricaoValida(detNivel.plano_descricao)}
                            onChange={e => updateRespostaDetalhe(qp.qp_id, { plano_descricao: e.target.value })}
                            onBlur={() => persistRespostaDetalhe(qp.qp_id)}
                            disabled={isModelo || isFechado}
                          />
                          <div className="d-flex justify-content-between">
                            <Form.Control.Feedback type={!!detNivel.plano_descricao && !planoDescricaoValida(detNivel.plano_descricao) ? 'invalid' : undefined} className={!!detNivel.plano_descricao && !planoDescricaoValida(detNivel.plano_descricao) ? 'd-block' : 'd-none'}>
                              Informe entre {PLANO_DESCRICAO_MIN} e {PLANO_DESCRICAO_MAX} caracteres.
                            </Form.Control.Feedback>
                            <span className="small text-muted ms-auto">{(detNivel.plano_descricao || '').length}/{PLANO_DESCRICAO_MAX}</span>
                          </div>
                        </Col>
                      </Row>
                    </>
                  )}

                  {tipoNivel === 'JUSTIFICATIVA' && (
                    <>
                      <div className="fw-semibold mb-2" style={{ color: '#6f42c1' }}>Justificativa</div>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="Descreva o motivo pelo qual este item não se aplica..."
                        value={detNivel.justificativa || ''}
                        onChange={e => updateRespostaDetalhe(qp.qp_id, { justificativa: e.target.value })}
                        onBlur={() => persistRespostaDetalhe(qp.qp_id)}
                        disabled={isModelo || isFechado}
                      />
                    </>
                  )}

                  {tipoNivel === 'EVIDENCIA' && (
                    <>
                      <div className="fw-semibold mb-2" style={{ color: '#198754' }}>Anexar evidência</div>
                      <Form.Control
                        type="file"
                        onChange={e => handleEvidenciaUpload(qp.qp_id, (e.target as HTMLInputElement).files?.[0] || null)}
                        disabled={isModelo || isFechado || evidenciaSaving === qp.qp_id}
                      />
                      <div className="small text-muted mt-1">
                        {evidenciaSaving === qp.qp_id ? (
                          'Enviando...'
                        ) : detNivel.evidencia_nome ? (
                          <>
                            Evidência anexada:{' '}
                            <span
                              role="button"
                              className="fw-semibold"
                              style={{ cursor: 'pointer', textDecoration: 'underline' }}
                              onClick={() => handleAbrirEvidenciaResposta(qp.qp_id)}
                            >
                              {detNivel.evidencia_nome}
                            </span>
                          </>
                        ) : (
                          'Campo opcional neste primeiro momento.'
                        )}
                      </div>
                      <div className="fw-semibold mb-2 mt-3" style={{ color: '#198754' }}>Comentários</div>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        maxLength={COMENTARIO_EVIDENCIA_MAX}
                        placeholder="Digite um comentário sobre esta pergunta (opcional)..."
                        value={detNivel.evidencia_comentario || ''}
                        onChange={e => updateRespostaDetalhe(qp.qp_id, { evidencia_comentario: e.target.value })}
                        onBlur={() => persistRespostaDetalhe(qp.qp_id)}
                        disabled={isModelo || isFechado}
                      />
                      <div className="d-flex justify-content-between">
                        <span className="small text-muted">Limite de 200 caracteres.</span>
                        <span className="small text-muted ms-auto">{(detNivel.evidencia_comentario || '').length}/{COMENTARIO_EVIDENCIA_MAX}</span>
                      </div>
                    </>
                  )}

                  {isPendingNivel && !isModelo && !isFechado && (
                    <Button
                      variant="primary"
                      size="sm"
                      className="mt-3"
                      onClick={handleNivelContinue}
                      disabled={tipoNivel === 'PLANO' && !planoDescricaoValida(detNivel.plano_descricao)}
                    >
                      Continuar →
                    </Button>
                  )}
                </div>
              )}

              {/* Checklist inline — SM-005: uma vez revelado, fica visível mesmo depois de
                  confirmado e de a(s) complementar(es) seguinte(s) aparecerem. checklistShown
                  nunca é limpo ao navegar (ver comentário em confirmarHerdadaEAvancar) —
                  showChecklistFor continua controlando só o botão de confirmar, abaixo. */}
              {checklistShown.has(qp.qp_id) && (
                <div className="mt-4 p-3 border rounded bg-light">
                  <h6 className="mb-3">Checklist</h6>
                  {(checklistData[qp.qp_id] || []).length === 0 ? (
                    <p className="text-muted">Carregando itens do checklist...</p>
                  ) : (
                    (checklistData[qp.qp_id] || []).map((req) => (
                      <div key={req.id} className="mb-3">
                        <div className="fw-semibold mb-2">{req.titulo}</div>
                        <Table size="sm" borderless className="mb-0">
                          <thead>
                            <tr className="text-muted small">
                              <th style={{ width: 50 }} className="text-center">Sim</th>
                              <th style={{ width: 50 }} className="text-center">Não</th>
                              <th style={{ width: 50 }} className="text-center">N/A</th>
                              <th>Item</th>
                            </tr>
                            {!isModelo && !isFechado && (
                              <tr className="small">
                                {(['SIM', 'NAO', 'NA'] as ChecklistItemResposta[]).map(opt => (
                                  <td key={opt} className="text-center p-0 pb-1">
                                    <Button
                                      variant="link"
                                      size="sm"
                                      className="p-0 text-decoration-none fw-semibold"
                                      onClick={() => handleMarcarTodosChecklist(qp.qp_id, req.itens, opt)}
                                    >
                                      Marcar todos
                                    </Button>
                                  </td>
                                ))}
                                <td></td>
                              </tr>
                            )}
                          </thead>
                          <tbody>
                            {req.itens.filter(item => item.ativo !== false).map(item => {
                              const respostaAtual = (checklistAnswers[qp.qp_id] || {})[item.id];
                              const tipoItem = respostaAtual ? detalheExigido(item.nivel_controle, respostaAtual) : null;
                              const detItem = (checklistDetalhes[qp.qp_id] || {})[item.id];
                              const corLegenda = tipoItem === 'PLANO' ? '#dc3545' : tipoItem === 'EVIDENCIA' ? '#198754' : '#6f42c1';
                              const textoLegenda = tipoItem === 'PLANO' ? 'Com Plano de ação.' : tipoItem === 'EVIDENCIA' ? 'Com evidência.' : 'Com Justificativa';
                              return (
                                <tr key={item.id}>
                                  {(['SIM', 'NAO', 'NA'] as ChecklistItemResposta[]).map(opt => (
                                    <td key={opt} className="text-center align-middle">
                                      <Form.Check
                                        type="radio"
                                        name={`checklist-${qp.qp_id}-${item.id}`}
                                        checked={respostaAtual === opt}
                                        onChange={() => handleChecklistAnswer(qp.qp_id, item.id, opt, item.nivel_controle)}
                                        disabled={isModelo || isFechado}
                                      />
                                    </td>
                                  ))}
                                  <td className="align-middle">
                                    {item.descricao}
                                    {tipoItem && (
                                      <>
                                        {' '}
                                        <span
                                          role="button"
                                          className="small fw-semibold"
                                          style={{ color: corLegenda, cursor: 'pointer', textDecoration: 'underline' }}
                                          onClick={() => setItemDetalheModal({ qpId: qp.qp_id, itemId: item.id, tipo: tipoItem })}
                                        >
                                          {textoLegenda}
                                          {tipoItem === 'EVIDENCIA' && detItem?.evidencia_nome ? ` (${detItem.evidencia_nome})` : ''}
                                        </span>
                                      </>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </div>
                    ))
                  )}
                  <div className="text-muted small mb-2">
                    Selecione uma opção por item — é obrigatório responder todos.
                  </div>
                  {!isModelo && !isFechado && showChecklistFor === qp.qp_id && (
                    <Button
                      variant="primary"
                      size="sm"
                      className="mt-2"
                      onClick={handleChecklistConfirm}
                      disabled={!isChecklistComplete(qp.qp_id)}
                    >
                      Confirmar Checklist e Avançar
                    </Button>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
        );
      })}

      {/* Modal: alterar resposta herdada (equivalência) em todos os questionários ativos */}
      <Modal show={!!herdadaModal} onHide={() => setHerdadaModal(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">Alterar resposta herdada</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Esta resposta foi <strong>herdada de outro questionário</strong>
          {herdadaModal && respostaHerdada[herdadaModal.qpId] ? ` (${respostaHerdada[herdadaModal.qpId]})` : ''}.
          Deseja alterar a resposta em <strong>todos os questionários ativos</strong>? As respostas
          equivalentes ficarão sincronizadas.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" size="sm" onClick={() => confirmarAlteracaoHerdada(false)}>
            Cancelar
          </Button>
          <Button variant="primary" size="sm" onClick={() => confirmarAlteracaoHerdada(true)}>
            Sim, alterar em todos
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal: detalhe (Plano/Evidência/Justificativa) de um item do checklist */}
      {itemDetalheModal && (() => {
        const { qpId, itemId, tipo } = itemDetalheModal;
        const item = (checklistData[qpId] || []).flatMap(req => req.itens).find(it => it.id === itemId);
        const det = (checklistDetalhes[qpId] || {})[itemId] || {};
        const corTitulo = tipo === 'PLANO' ? '#dc3545' : tipo === 'EVIDENCIA' ? '#198754' : '#6f42c1';
        return (
          <Modal show onHide={() => setItemDetalheModal(null)} centered>
            <Modal.Header closeButton>
              <Modal.Title className="fs-6">
                <span style={{ color: corTitulo }}>
                  {tipo === 'PLANO' ? 'Defina um Plano de Ação' : tipo === 'EVIDENCIA' ? 'Anexar evidência' : 'Justificativa'}
                </span>
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {item && <div className="text-muted small mb-3">{item.descricao}</div>}

              {tipo === 'PLANO' && (
                <Row className="g-2">
                  <Col md={12} className="mb-2">
                    <Form.Label className="small">Data limite</Form.Label>
                    <Form.Control
                      type="date"
                      value={det.plano_data_limite || ''}
                      min={new Date().toISOString().slice(0, 10)}
                      onKeyDown={e => e.preventDefault()}
                      onChange={e => updateChecklistDetalhe(qpId, itemId, { plano_data_limite: e.target.value })}
                      disabled={isModelo || isFechado}
                    />
                  </Col>
                  <Col md={12} className="mb-2">
                    <Form.Label className="small">Nome do Responsável</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Digite o nome completo"
                      value={det.plano_responsavel_nome || ''}
                      onChange={e => updateChecklistDetalhe(qpId, itemId, { plano_responsavel_nome: e.target.value })}
                      disabled={isModelo || isFechado}
                    />
                  </Col>
                  <Col md={12}>
                    <Form.Label className="small">E-mail</Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="nome@empresa.com"
                      value={det.plano_responsavel_email || ''}
                      isInvalid={!!det.plano_responsavel_email && !emailValido(det.plano_responsavel_email)}
                      onChange={e => updateChecklistDetalhe(qpId, itemId, { plano_responsavel_email: e.target.value })}
                      disabled={isModelo || isFechado}
                    />
                    <Form.Control.Feedback type="invalid">E-mail inválido.</Form.Control.Feedback>
                  </Col>
                  <Col md={12} className="mt-2">
                    <Form.Label className="small">Descrição do Plano de Ação (campo de texto livre)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      maxLength={PLANO_DESCRICAO_MAX}
                      placeholder="Descreva as ações que serão tomadas..."
                      value={det.plano_descricao || ''}
                      isInvalid={!!det.plano_descricao && !planoDescricaoValida(det.plano_descricao)}
                      onChange={e => updateChecklistDetalhe(qpId, itemId, { plano_descricao: e.target.value })}
                      disabled={isModelo || isFechado}
                    />
                    <div className="d-flex justify-content-between">
                      <Form.Control.Feedback type={!!det.plano_descricao && !planoDescricaoValida(det.plano_descricao) ? 'invalid' : undefined} className={!!det.plano_descricao && !planoDescricaoValida(det.plano_descricao) ? 'd-block' : 'd-none'}>
                        Informe entre {PLANO_DESCRICAO_MIN} e {PLANO_DESCRICAO_MAX} caracteres.
                      </Form.Control.Feedback>
                      <span className="small text-muted ms-auto">{(det.plano_descricao || '').length}/{PLANO_DESCRICAO_MAX}</span>
                    </div>
                  </Col>
                </Row>
              )}

              {tipo === 'JUSTIFICATIVA' && (
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Descreva o motivo pelo qual este item não se aplica..."
                  value={det.justificativa || ''}
                  onChange={e => updateChecklistDetalhe(qpId, itemId, { justificativa: e.target.value })}
                  disabled={isModelo || isFechado}
                />
              )}

              {tipo === 'EVIDENCIA' && (
                <>
                  <Form.Control
                    type="file"
                    onChange={e => handleChecklistItemEvidenciaUpload(qpId, itemId, (e.target as HTMLInputElement).files?.[0] || null)}
                    disabled={isModelo || isFechado || evidenciaSavingItem === itemId}
                  />
                  <div className="small text-muted mt-1">
                    {evidenciaSavingItem === itemId ? (
                      'Enviando...'
                    ) : det.evidencia_nome ? (
                      <>
                        Evidência anexada:{' '}
                        <span
                          role="button"
                          className="fw-semibold"
                          style={{ cursor: 'pointer', textDecoration: 'underline' }}
                          onClick={() => handleAbrirEvidenciaChecklistItem(qpId, itemId)}
                        >
                          {det.evidencia_nome}
                        </span>
                      </>
                    ) : (
                      'Campo opcional neste primeiro momento.'
                    )}
                  </div>
                  <div className="fw-semibold mb-2 mt-3" style={{ color: '#198754' }}>Comentários</div>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    maxLength={COMENTARIO_EVIDENCIA_MAX}
                    placeholder="Digite um comentário sobre este item (opcional)..."
                    value={det.evidencia_comentario || ''}
                    onChange={e => updateChecklistDetalhe(qpId, itemId, { evidencia_comentario: e.target.value })}
                    disabled={isModelo || isFechado}
                  />
                  <div className="d-flex justify-content-between">
                    <span className="small text-muted">Limite de 200 caracteres.</span>
                    <span className="small text-muted ms-auto">{(det.evidencia_comentario || '').length}/{COMENTARIO_EVIDENCIA_MAX}</span>
                  </div>
                </>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="primary"
                size="sm"
                onClick={() => { if (!isFechado) persistChecklistDetalhe(qpId, itemId); setItemDetalheModal(null); }}
                disabled={tipo === 'PLANO' && !planoDescricaoValida(det.plano_descricao)}
              >
                {isFechado ? 'Fechar' : 'OK'}
              </Button>
            </Modal.Footer>
          </Modal>
        );
      })()}

      {/* Auditoria (SM-004): 1x por bloco, associada à pergunta PRINCIPAL/controle —
          não por complementar nem por item de checklist individual. */}
      {modoAuditoria && currentPrincipal && (() => {
        const dadosAtual = auditoriaDados[currentPrincipal.qp_id] || AuditoriaItemVazio;
        return (
          <Card className="mb-3 border-primary">
            <Card.Body>
              <Card.Title className="fs-6 text-primary mb-3">Auditoria</Card.Title>
              <Row className="g-3">
                <Col md={5}>
                  <Form.Label className="small fw-semibold">Classificação de Não Conformidade</Form.Label>
                  <SearchSelectField
                    placeholder="Selecione a classificação..."
                    options={classificacoesConformidade}
                    value={dadosAtual.classificacao_conformidade_id != null ? String(dadosAtual.classificacao_conformidade_id) : ''}
                    onSelect={opt => {
                      const cid = opt ? Number(opt.key) : null;
                      updateAuditoriaField(currentPrincipal.qp_id, { classificacao_conformidade_id: cid });
                      persistAuditoria(currentPrincipal.qp_id, { classificacao_conformidade_id: cid });
                    }}
                  />
                </Col>
                <Col md={7}>
                  <Form.Label className="small fw-semibold">Comentários do Auditor</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    maxLength={350}
                    placeholder="Descreva a observação do auditor para este item..."
                    value={dadosAtual.comentario_auditor || ''}
                    onChange={e => updateAuditoriaField(currentPrincipal.qp_id, { comentario_auditor: e.target.value.slice(0, 350) })}
                    onBlur={() => persistAuditoria(currentPrincipal.qp_id)}
                  />
                  <Form.Text className="text-muted">{(dadosAtual.comentario_auditor || '').length}/350 caracteres</Form.Text>
                </Col>
              </Row>

              <div className="mt-3">
                <Form.Label className="small fw-semibold d-block">Anexos</Form.Label>
                {dadosAtual.anexos.length > 0 && (
                  <div className="d-flex flex-wrap gap-2 mb-2">
                    {dadosAtual.anexos.map(anexo => (
                      <span key={anexo.id} className="d-inline-flex align-items-center gap-1 border rounded px-2 py-1 small">
                        <Button variant="link" size="sm" className="p-0" onClick={() => handleAuditoriaAnexoAbrir(anexo.id)}>
                          <FiPaperclip /> {anexo.arquivo_nome}
                        </Button>
                        <Button variant="link" size="sm" className="p-0 text-danger"
                          onClick={() => handleAuditoriaAnexoRemover(currentPrincipal.qp_id, anexo.id)}>
                          <FiX />
                        </Button>
                      </span>
                    ))}
                  </div>
                )}
                <Form.Control
                  type="file"
                  multiple
                  disabled={auditoriaUploading}
                  onChange={e => {
                    handleAuditoriaAnexoUpload(currentPrincipal.qp_id, (e.target as HTMLInputElement).files);
                    (e.target as HTMLInputElement).value = '';
                  }}
                />
                {auditoriaUploading && <div className="small text-muted mt-1">Enviando...</div>}
              </div>
            </Card.Body>
          </Card>
        );
      })()}

      {/* Navegação */}
      <Row className="g-2">
        <Col>
          {principalIndex > 0 && (
            <Button variant="outline-secondary" onClick={handleBack}>
              ← Voltar
            </Button>
          )}
        </Col>
        <Col className="text-end">
          {showContinueButton ? (
            <Button
              variant="primary"
              onClick={goToNextPrincipal}
              // Fechado não bloqueia aqui: é só navegação (nenhum campo é editável), então o
              // usuário em modo "Ver" precisa conseguir avançar pelas perguntas já respondidas.
              disabled={isModelo || !blockAnswered}
            >
              Continuar questionário →
            </Button>
          ) : (
            <Button variant="primary" onClick={handleAvancar}>
              Avançar →
            </Button>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default AnswerQuestionnairePage;
